import {DndContext, useDroppable} from "@dnd-kit/core"
import React, {useRef, useCallback, useEffect} from "react"
import {Box, Grid, Typography} from "@mui/material"
import TimelineComponent from "../../components/general/TimelineComponent"
import DraggableEvent from "./DraggableEvent"
import { fileServerUrlParser } from "../../misc/utils"

export const widgetDragAndDropDynamikPrototype = [
        {
          "key": "startDate",
          "type": "INTEGER",
          "label": "Start Datum",
          "size": 3
        },
        {
          "key": "endDate",
          "type": "INTEGER",
          "label": "End Datum",
          "size": 3
        }
      ]

function calculateDateProgressPercentage(currentDate, startDate, endDate) {
    const startMs = new Date(`${startDate}-01-01`).getTime()
    const endMs = new Date(`${endDate}-01-01`).getTime()
    const currentMs = new Date(currentDate).getTime()
    const durationMs = endMs - startMs

    if (durationMs <= 0) {
        return currentMs >= endMs ? 100 : 0
    }
    const elapsedMs = currentMs - startMs
    let percentage = (elapsedMs / durationMs) * 100
    percentage = Math.max(0, percentage)
    percentage = Math.min(100, percentage)

    return percentage
}

const calcPosition = (timelineRef, currentDate, startDate, endDate) => {
    const x =
        (timelineRef.current?.offsetWidth *
            calculateDateProgressPercentage(currentDate, startDate, endDate)) /
        100
    const y = -180

    return {x, y}
}

const WidgetTimelineBoard = ({widget, value, updateData, getDataFromPointer, onValueChange}) => {
    const timelineRef = useRef(null)
    const START_YEAR = +(widget.startDate ?? 1980)
    const END_YEAR = +(widget.endDate ?? 2050)
    const {setNodeRef: setSourceNodeRef} = useDroppable({
        id: "source",
    })

    const getDateFromCursorPosition = useCallback(event => {
        if (!timelineRef.current) return null
        const {left, width} = timelineRef.current.getBoundingClientRect()
        const cursorX = event.activatorEvent.clientX + event.delta.x + left
        const percentage = Math.min(Math.max(0, cursorX / width), 1)
        const totalYears = END_YEAR - START_YEAR
        const currentYearFloat = START_YEAR + percentage * totalYears
        const dayOfYear = (currentYearFloat % 1) * 365

        const date = new Date(
            Math.floor(currentYearFloat),
            0,
            1 + Math.round(dayOfYear),
        )

        return date
    }, [])

    const handleDragEnd = event => {
        const {active, delta} = event
        const dropDate = getDateFromCursorPosition(event)

        onValueChange(
            value.map(item => {
                if (item.id === active.id) {
                    const position = item.position
                        ? item.position
                        : calcPosition(
                            timelineRef,
                            item.date,
                            START_YEAR,
                            END_YEAR,
                        )

                    return {
                        ...item,
                        position: {
                            x: (position?.x || 0) + delta.x,
                            y: (position?.y || 0) + delta.y,
                        },
                        date: dropDate.toISOString(),
                    }
                }
                return item
            }),
        )
    }

    useEffect(() => {
        updateData({
            width: timelineRef.current.clientWidth,
            height: timelineRef.current.clientHeight,
        }, "/appstate/lebensmeilensteine/container")
    }, [])

    return (
        <DndContext onDragEnd={handleDragEnd}>
            <Box
                ref={node => {
                    timelineRef.current = node
                }}
                sx={{mt: 3, position: "relative"}}
            >
                <TimelineComponent
                    startDate={widget.startDate}
                    endDate={widget.endDate}
                />
                <Grid container ref={setSourceNodeRef}>
                    {value?.map(item => (
                        <DraggableEvent
                            doubleClickAction={widget?.itemDoubleClickAction}
                            key={item.id}
                            item={item}
                            defaultPosition={calcPosition(
                                timelineRef,
                                item.date,
                                START_YEAR,
                                END_YEAR,
                            )}
                            getDataFromPointer={getDataFromPointer}
                        >
                            <Grid container direction="column" alignItems="center" justifyContent="center">
                                {item.icon && <img src={fileServerUrlParser(item.icon)}/>}
                                <Typography fontSize="14px" color="inherit">
                                    {item.longname}
                                </Typography>
                            </Grid>
                        </DraggableEvent>
                    ))}
                </Grid>
            </Box>
        </DndContext>
    )
}

export default WidgetTimelineBoard
